import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import { TableHeader } from '@Features/generics/generics.type';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CircularProgress, IconButton, TableCell } from '@mui/material';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { apiData, errorMessage, StatusChip } from '../Subscriptions/subscriptions.shared';
import useStudentList from '../Subscriptions/useStudentList';

type Classroom = {
	id: number;
	title: string;
	label: string;
	instructor?: { user?: { first_name?: string; last_name?: string } };
	pivot?: { pivot_active?: boolean; pivot_created_at?: Date };
};

type Props = {
	studentId: number;
	/** the course columns; the classroom row spans them */
	tableHeads: TableHeader[];
	/** one course row (the app's own cells + actions); call onChanged after an action */
	renderCourse: (course: any, onChanged: () => void) => React.ReactNode;
};

/**
 * Student Courses tab, grouped by classroom: each classroom the student is in
 * is a row that expands (one at a time) to its courses — the same pattern as
 * the Subscriptions tab's bundles and standalone courses. Shared by both
 * portals (the instructor app imports this file); each app renders its own
 * course cells through `renderCourse`.
 */
const ClassroomGroupedCourses: React.FC<Props> = ({ studentId, tableHeads, renderCourse }) => {
	const { displaySnackbar } = useSnackbar();
	const { rows: classrooms, meta, load } = useStudentList<Classroom>(
		studentId,
		'classrooms',
		[],
		'Unable to load classrooms'
	);
	const [expanded, setExpanded] = useState<number | null>(null);
	const [courses, setCourses] = useState<any[]>([]);
	const [loadingCourses, setLoadingCourses] = useState(false);

	const loadCourses = useCallback(
		async (classroomId: number) => {
			setLoadingCourses(true);
			try {
				const response = await api.post(`user/profile/${studentId}/courses`, {
					page: 1,
					perPage: 500,
					filters: [{ 'courses.classroom_id': classroomId }],
				});
				setCourses(apiData<any[]>(response) || []);
			} catch (error: any) {
				displaySnackbar('error', errorMessage(error, 'Unable to load courses'));
			} finally {
				setLoadingCourses(false);
			}
		},
		[studentId, displaySnackbar]
	);

	const toggle = (classroomId: number) => {
		if (expanded === classroomId) {
			setExpanded(null);
			return;
		}
		setExpanded(classroomId);
		setCourses([]);
		loadCourses(classroomId);
	};

	// the classroom row: title across most course columns, summary in the rest
	// (+ the actions column); course rows use every column
	const span = tableHeads.length;
	const titleSpan = Math.max(2, span - 3);
	const summarySpan = span + 1 - titleSpan;

	const renderClassroom = (classroom: Classroom) => {
		const open = expanded === classroom.id;
		const instructor = [classroom.instructor?.user?.first_name, classroom.instructor?.user?.last_name]
			.filter(Boolean)
			.join(' ');
		return (
			<React.Fragment key={`cl${classroom.id}`}>
				<MainTableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(classroom.id)}>
					<TableCell colSpan={titleSpan}>
						<TitleCell>
							<IconButton size="small" aria-label={open ? 'Collapse classroom' : 'Expand classroom'}>
								{open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
							</IconButton>
							<div>
								<Title>{classroom.title}</Title>
								<Note>
									{classroom.label}
									{instructor && ` · ${instructor}`}
								</Note>
							</div>
						</TitleCell>
					</TableCell>
					<TableCell colSpan={summarySpan}>
						<Summary>
							{classroom.pivot?.pivot_created_at && (
								<span>
									Enrolled <DateTimeFormatter date={classroom.pivot.pivot_created_at} />
								</span>
							)}
							<StatusChip status={classroom.pivot?.pivot_active === false ? 'banned' : 'active'} />
						</Summary>
					</TableCell>
				</MainTableRow>
				{open &&
					(loadingCourses && !courses.length ? (
						<MainTableRow>
							<TableCell colSpan={span + 1} align="center">
								<CircularProgress size={22} />
							</TableCell>
						</MainTableRow>
					) : courses.length === 0 ? (
						<MainTableRow>
							<TableCell colSpan={span + 1} align="center">
								<Muted>No courses</Muted>
							</TableCell>
						</MainTableRow>
					) : (
						courses.map((course) => (
							<React.Fragment key={`co${course.id}`}>{renderCourse(course, () => loadCourses(classroom.id))}</React.Fragment>
						))
					))}
			</React.Fragment>
		);
	};

	return (
		<MainTable
			onPageChangeRequest={load}
			tableFilters={[]}
			tableHeads={tableHeads}
			totalPages={Number(meta.last_page || 1)}
			total={Number(meta.total || 0)}
			RowsperPage={Number(meta.per_page || 10)}
			pageNum={Number(meta.current_page || 1)}
			$hasActions
			renderItems={() =>
				classrooms.length === 0 ? (
					<MainTableRow>
						<TableCell colSpan={span + 1}>
							<NoResults />
						</TableCell>
					</MainTableRow>
				) : (
					classrooms.map(renderClassroom)
				)
			}
		/>
	);
};

const TitleCell = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	text-align: start;
`;
const Title = styled.p`
	font-weight: 600;
	white-space: normal;
	overflow-wrap: anywhere;
`;
const Note = styled.p`
	font-size: 0.75rem;
	color: #6e7577;
	white-space: normal;
`;
const Summary = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 1.5rem;
	color: #6e7577;
	font-size: 0.85rem;
`;
const Muted = styled.span`
	color: #a4a4a4;
	font-size: 0.8rem;
`;

export default ClassroomGroupedCourses;
